import { format, parseISO } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const COLUMN_WIDTHS = {
  orderTaskNumber: 130,
  productCode: 130,
  productName: 120,
  specification: 150,
  orderQty: 60,
  plannedQty: 60,
  cumulativeCompleted: 70,
  orderPlan: 70,
  orderDate: 90,
  plannedEndDate: 90,
  plannedHours: 60,
  equipment: 90,
  date: 72,
};

export function getColumnWidths() {
  return COLUMN_WIDTHS;
}

function safeDate(value) {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatDateShort(value) {
  const d = safeDate(value);
  return d ? format(d, 'yyyy.M.d') : '';
}

export function groupSchedulesForExport({ schedules, dates, workCenters }) {
  const dateKeys = dates.map((d) => format(d, 'yyyy-MM-dd'));

  // Filter schedules that fall inside the displayed date range
  const inRange = schedules.filter((s) => {
    const d = safeDate(s.scheduled_date);
    if (!d) return false;
    return dateKeys.includes(format(d, 'yyyy-MM-dd'));
  });

  const rowsByWorkCenter = [];

  workCenters.forEach((wc) => {
    const wcSchedules = inRange.filter((s) => {
      const swcId = s.work_center_id?._id || s.work_center_id;
      return swcId === wc._id || s.work_center_name === wc.name;
    });

    if (wcSchedules.length === 0) return;

    const groups = new Map();
    const groupSchedulesMap = new Map();

    wcSchedules.forEach((s) => {
      const isCustom = s.source === 'custom' || !s.production_order_id;
      const key = isCustom
        ? `custom-${s.finished_product_code || ''}-${s.specification || ''}-${s.step_name || ''}-${s._id}`
        : `${s.production_order_id}-${s.item_id}-${s.step_sequence}`;

      if (!groupSchedulesMap.has(key)) groupSchedulesMap.set(key, []);
      groupSchedulesMap.get(key).push(s);

      if (!groups.has(key)) {
        const orderDate = isCustom
          ? (s.notes || formatDateShort(s.created_at))
          : formatDateShort(s.created_at);

        const plannedEndDate = isCustom
          ? formatDateShort(s.scheduled_date)
          : '';

        const orderPlan = isCustom ? 0 : (s.item_planned_quantity || 0);

        groups.set(key, {
          isCustom,
          orderTaskNumber: isCustom ? '临时新增' : (s.production_order_number || ''),
          productCode: s.finished_product_code || '',
          productName: s.finished_product_name || '',
          specification: s.specification || '',
          orderQty: orderPlan,
          plannedQty: orderPlan,
          cumulativeCompleted: 0,
          orderPlan,
          orderDate,
          plannedEndDate,
          plannedHours: 0,
          equipment: wc.name,
          workCenter: wc,
          dateMap: Object.fromEntries(dateKeys.map((dk) => [dk, 0])),
          completedMap: Object.fromEntries(dateKeys.map((dk) => [dk, 0])),
          scrappedMap: Object.fromEntries(dateKeys.map((dk) => [dk, 0])),
          totalAssigned: 0,
          totalCompleted: 0,
          totalScrapped: 0,
        });
      }

      const group = groups.get(key);
      const dk = format(safeDate(s.scheduled_date), 'yyyy-MM-dd');
      const assignedQty = s.assigned_quantity || 0;
      const completedQty = s.completed_quantity || 0;
      const scrappedQty = s.scrapped_quantity || 0;
      group.dateMap[dk] = (group.dateMap[dk] || 0) + assignedQty;
      group.completedMap[dk] = (group.completedMap[dk] || 0) + completedQty;
      group.scrappedMap[dk] = (group.scrappedMap[dk] || 0) + scrappedQty;
      group.totalAssigned += assignedQty;
      group.totalCompleted += completedQty;
      group.totalScrapped += scrappedQty;
      group.plannedHours = Math.max(group.plannedHours, s.standard_time || 0);

      if (isCustom) {
        group.orderQty = group.totalAssigned;
        group.plannedQty = group.totalAssigned;
        group.orderPlan = group.totalAssigned;
      }
    });

    // Calculate real cumulative completion per order-item-step, avoiding double-counting
    // rolled-over schedules whose cumulative value already lives in their successor.
    groups.forEach((group, key) => {
      const groupSchedules = groupSchedulesMap.get(key) || [];
      const cumulative = computeStepCumulative(groupSchedules, inRange);
      group.cumulativeCompleted = cumulative.completed;
    });

    const rows = Array.from(groups.values());
    const groupTotals = rows.reduce(
      (acc, r) => ({
        totalAssigned: acc.totalAssigned + r.totalAssigned,
        totalCompleted: acc.totalCompleted + r.totalCompleted,
        totalScrapped: acc.totalScrapped + r.totalScrapped,
        totalPlannedQty: acc.totalPlannedQty + r.plannedQty,
      }),
      { totalAssigned: 0, totalCompleted: 0, totalScrapped: 0, totalPlannedQty: 0 }
    );

    rowsByWorkCenter.push({
      workCenter: wc,
      rows,
      summary: groupTotals,
    });
  });

  return { dateKeys, rowsByWorkCenter };
}

export function formatDayHeader(date) {
  return {
    date: format(date, 'M/d'),
    weekday: format(date, 'EEE', { locale: zhCN }),
  };
}

export function renderRestMarker(_date, value) {
  return value > 0 ? String(value) : '';
}

export function calculateAchievementRate(completed, assigned) {
  if (!assigned) return '0.0%';
  return `${((completed / assigned) * 100).toFixed(1)}%`;
}

export function calculateDefectRate(scrapped, completed) {
  const total = completed + scrapped;
  if (!total) return '0.0%';
  return `${((scrapped / total) * 100).toFixed(1)}%`;
}

/**
 * Compute the real cumulative completed/scrapped quantity for a group of schedules
 * that belong to the same order-item-step.
 *
 * `schedule.completed_quantity` is cumulative up to the day it was settled. A schedule
 * that has been rolled over already handed its cumulative value to its successor, so
 * it must be excluded unless the successor is not present in `allSchedules`.
 */
export function computeStepCumulative(groupSchedules, allSchedules) {
  const allById = new Map(allSchedules.map((s) => [s._id, s]));
  const hasRolledSuccessor = (sch) =>
    Array.from(allById.values()).some(
      (candidate) => candidate.rolled_from_schedule_id === sch._id
    );

  return groupSchedules.reduce(
    (acc, s) => {
      if (s.status === 'cancelled') return acc;
      if (s.status === 'rolled_over' && hasRolledSuccessor(s)) return acc;
      acc.completed += s.completed_quantity || 0;
      acc.scrapped += s.scrapped_quantity || 0;
      return acc;
    },
    { completed: 0, scrapped: 0 }
  );
}

export function computeDailyGroupSummaries(
  rows,
  dates,
  settledDateMap = {},
  workCenterName = ''
) {
  return dates.map((date) => {
    const dk = format(date, 'yyyy-MM-dd');
    const settlement = settledDateMap[dk];
    const isSettled = Boolean(
      settlement && Array.isArray(settlement.items)
    );

    if (isSettled) {
      // Use the official settlement record so exported stats reflect
      // the actual daily completion / scrap numbers, not cumulative values.
      const items = settlement.items.filter(
        (item) =>
          !workCenterName ||
          (item.work_center_name || '').trim() === workCenterName.trim()
      );
      const totalAssigned = items.reduce(
        (sum, item) => sum + (item.assigned_quantity || 0),
        0
      );
      const totalCompleted = items.reduce(
        (sum, item) => sum + (item.completed_quantity || 0),
        0
      );
      const totalScrapped = items.reduce(
        (sum, item) => sum + (item.scrapped_quantity || 0),
        0
      );
      return {
        dateKey: dk,
        totalAssigned,
        totalCompleted,
        totalScrapped,
        isSettled: true,
      };
    }

    let totalAssigned = 0;
    let totalCompleted = 0;
    let totalScrapped = 0;
    rows.forEach((row) => {
      totalAssigned += row.dateMap[dk] || 0;
      totalCompleted += row.completedMap[dk] || 0;
      totalScrapped += row.scrappedMap[dk] || 0;
    });
    return {
      dateKey: dk,
      totalAssigned,
      totalCompleted,
      totalScrapped,
      isSettled: false,
    };
  });
}

export function getExportTitle(startDate, endDate) {
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  let rangeText;
  if (sameMonth) {
    rangeText = format(startDate, 'yyyy年M月');
  } else if (sameYear) {
    rangeText = `${format(startDate, 'yyyy年M月d日')}-${format(endDate, 'M月d日')}`;
  } else {
    rangeText = `${format(startDate, 'yyyy年M月d日')}-${format(endDate, 'yyyy年M月d日')}`;
  }

  return `${rangeText}生产日计划与完成统计表`;
}

export async function captureScheduleImage(container, filename) {
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
  });

  const link = document.createElement('a');
  link.download = filename || '生产日计划.png';
  link.href = canvas.toDataURL('image/png');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
